import React, { useContext, useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import axios from "axios";

import { getAccessToken } from "lib/token";
import axiosLC, {
  FIND_USER,
  SUBMIT_FORM,
  UPDATE_USER_FROM_FORM,
  UPLOAD_USER_FILE,
} from "services";

import {
  CheckboxField,
  DateField,
  DataPolicyCheck,
  FileField,
  FormSteps,
  FormHeader,
  GeneralButton,
  Header,
  ModalSuccess,
  ModalDataConfirm,
  NumberField,
  ModalError,
  TextInput,
  Loader,
  SelectField,
  SnackbarRequiredFieldError,
  FormHeaderFinished,
} from "components";
import { IFormSchema, IPayload, IError, IResultData } from "../types/types";
import { getIndex, getFieldsRequired } from "helpers";
import { useFormPages, useFormData } from "hooks";
import { ImageContext } from "context";
import styles from "../styles/GeneralButton.module.scss";
import { SimpleTextInput } from "components/SimpleTextInput";

const App = () => {
  const methods = useForm();
  const { filesContext } = useContext(ImageContext);
  const { values, feId, fe, userId, ref, branchOffice, loading, setLoading } =
    useFormData();
  const { pages } = useFormPages(values);
  const [uid, setUid] = useState<string>("");
  const [nid, setNid] = useState<string>("");
  const [clientId, setClientId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<Array<number>>([]);
  const [openModal, setOpenModal] = useState(false);
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [success, setSuccess] = useState(false);
  const [discarded, setDiscarded] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [sendData, setSendData] = useState({
    name: "",
    lastname: "",
    nit: "",
    phone: "",
    email: "",
  });
  const [errorAxios, setErrorAxios] = useState({
    error: false,
    message: "",
  });
  const color = localStorage.getItem("primaryColor") || "";

  useEffect(() => {
    // RETURNS THE INDEX FOR EACH PAGE
    const { pagesIndex } = getIndex(values);
    setSteps(pagesIndex);
  }, [values]);

  useEffect(() => {
    if (userId) {
      setClientId(userId);
    }
  }, [userId]);

  const onSubmitData = async () => {
    const data = methods.getValues();

    const schemaValues = values.schema.filter(
      (item) => item.component.type !== "file"
    );
    const schemaFiles = values.schema.filter(
      (item) => item.component.type === "file"
    );

    try {
      setLoading(true);
      const payload: IPayload = {
        properties: {
          financialEntityId: feId,
          loanProductName: values.loanProductName,
          riskProfileId: values.riskProfileId,
          ref,
        },
        leadData: {
          name: data.name,
          lastname: data.lastname,
          nit: data.nit,
          business_name: data.name || "--",
          phone: data.phone || "--",
          email: data.email || "--",
          address: data.address || "--",
          image: "",
          lead_status: "not_assigned",
          created_at: new Date().toISOString(),
          business_entity: "natural",
          ecosystem: fe.ecosystem,
          branch_office: branchOffice,
        },
        variablesData: [
          ...schemaValues
            ?.filter(
              (item: IFormSchema) =>
                ![
                  "name",
                  "lastname",
                  "nit",
                  "phone",
                  "email",
                  "address",
                ]?.includes(item?.component?.name)
            )
            .filter(
              (item: IFormSchema) => !["page"]?.includes(item?.component?.type)
            )
            .map((item: IFormSchema) => {
              return {
                value: data[item?.component.name],
                label: item?.label,
                type: "string",
                key: item?.component.name,
              };
            }),
        ],
      };

      // ACCESS TOKEN
      //if (!axiosLC.defaults.headers.common["Authorization"]) {
      const token = await getAccessToken();
      axiosLC.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      //}

      // SUBMIT FORM
      let userUid: string;
      const _userId = userId || clientId;
      if (_userId) {
        // rome-ignore lint/performance/noDelete: <explanation>
        delete payload.properties;
        // rome-ignore lint/performance/noDelete: <explanation>
        delete payload.leadData;
        payload["financialEntityId"] = feId;
        payload["userId"] = _userId;
        userUid = _userId;
        await axiosLC.post(UPDATE_USER_FROM_FORM, payload);
        localStorage.removeItem("onboarding");
        setDiscarded(true);
      } else {
        const resultForm = await axiosLC.post(SUBMIT_FORM, payload);
        localStorage.removeItem("onboarding");
        const resultData: IResultData = resultForm.data;
        userUid = resultData.id;
        resultData.lead_status === "discarded" && setDiscarded(true);
        setUid(resultData.id);
      }

      // UPLOAD USER DOCS
      if (schemaFiles.length > 0) {
        const filesPromises: Promise<void>[] = [];
        schemaFiles.forEach((item) => {
          const files: FileList = filesContext[item.component.name];
          const formData = new FormData();
          const _uid = userId ? userId : userUid;
          formData.append("userId", _uid);
          formData.append("financialEntityId", feId);
          formData.append("category", "profile");
          formData.append("filename", item.label);
          if (files) {
            const file = files[0];
            formData.append("file", file);
          }
          filesPromises.push(axiosLC.post(UPLOAD_USER_FILE, formData));
        });
        // const resultDocs = await Promise.all(filesPromises);

        for (const promise of filesPromises) {
          await promise;
        }
      }
      setSuccess(true);
      setLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log("isAxiosError", error?.response?.data);
        const messageError: string = (error?.response?.data as IError)?.message;
        if (
          error?.response?.status === 400 &&
          messageError.includes("There is already another Lead with")
        ) {
          // BUSCAR EL USUARIO EXISTENTE
          try {
            const token = await getAccessToken();
            const result = await axiosLC.post(
              FIND_USER,
              {
                nid: sendData.nit,
                financialEntityId: feId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            const { id } = result.data;
            setUid(id);
            setSuccess(true);
            setLoading(false);
          } catch (error) {
            setLoading(false);
            console.log("[error] onFindUserByNid", error);
            setErrorAxios({
              error: true,
              message:
                "Algo malo paso. Vuelve a intentarlo mas tarde o escríbenos a nuestras líneas de atención.",
            });
          }
        }
        if (
          error?.response?.status === 400 &&
          messageError.includes("User with nit")
        ) {
          // BUSCAR EL USUARIO EXISTENTE
          try {
            const token = await getAccessToken();
            const result = await axiosLC.post(
              FIND_USER,
              {
                nid: sendData.nit,
                financialEntityId: feId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            const { id } = result.data;
            setUid(id);
            setSuccess(true);
            setLoading(false);
          } catch (error) {
            setLoading(false);
            console.log("[error] onFindUserByNid", error);
            setErrorAxios({
              error: true,
              message:
                "Algo malo paso. Vuelve a intentarlo mas tarde o escríbenos a nuestras líneas de atención.",
            });
          }
        }
        setLoading(false);
        if (
          error?.response?.status === 400 &&
          messageError.includes("Missing required field: address")
        ) {
          setErrorAxios({
            error: true,
            message:
              "Hubo un problema al completar la solicitud, verifique que hayan sido completados los campos marcados como requeridos.",
          });
        }
        if (error?.response?.status === 500) {
          setErrorAxios({
            error: true,
            message:
              "Algo malo paso. Vuelve a intentarlo mas tarde o escríbenos a nuestras líneas de atención.",
          });
        }
      }
    }
  };

  const confirmData = () => {
    const userData = methods.getValues();
    const { isRequired } = getFieldsRequired(pages, userData, currentStep);

    if (isRequired) {
      setFieldsRequired(isRequired);
    } else {
      setOpenModal(true);
      setSendData({
        name: userData.name,
        lastname: userData.lastname,
        nit: userData.nit,
        phone: userData.phone,
        email: userData.email,
      });
    }
  };

  const onNextStep = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const userData = methods.getValues();
    const { isRequired } = getFieldsRequired(pages, userData, currentStep);
    isRequired
      ? setFieldsRequired(isRequired)
      : setCurrentStep((currentPage) => currentPage + 1);
  };

  const onBackStep = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setCurrentStep((currentPage) => currentPage - 1);
  };

  const onClose = () => {
    // window.location.reload();
    setSuccess(false);
    setDiscarded(false);
    setCurrentStep(0);
    setIsFinished(true);
  };

  const onFindUserByNid = async () => {
    if (!feId || !nid) {
      return;
    }
    try {
      const token = await getAccessToken();
      const result = await axiosLC.post(
        FIND_USER,
        {
          nid,
          financialEntityId: feId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { id } = result.data;
      setClientId(id);
    } catch (error) {
      console.log("[error] onFindUserByNid", error);
    }
  };

  if (loading) {
    return <Loader advanced />;
  }

  if (isFinished) {
    return (
      <>
        <Header />
        <FormHeaderFinished />
      </>
    );
  }

  if (!clientId && values?.formType === "linkedUser") {
    return (
      <>
        <Header />
        <FormHeader />
        <SimpleTextInput
          id={"NID"}
          name={"nid"}
          label={"Documento de identidad"}
          required
          value={nid}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setNid(e.target.value);
          }}
        />
        <GeneralButton
          onButtonClick={onFindUserByNid}
          disabled={false}
          text="Siguiente paso"
          type="button"
          background={color}
          border={color}
          textColor="#FFFFFF"
          cursor="pointer"
        />
      </>
    );
  }

  return (
    <>
      <Header />
      {success ? (
        <>
          <ModalSuccess
            feId={feId}
            userId={uid}
            discarded={discarded}
            onClose={onClose}
          />
        </>
      ) : values ? (
        <FormProvider {...methods}>
          <form>
            {steps?.length > 1 && (
              <FormSteps activeStep={currentStep} steps={steps} />
            )}
            <FormHeader />

            {pages?.map((page: IFormSchema[], index: number) => (
              // rome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              <React.Fragment key={index}>
                {index === currentStep && (
                  <>
                    {page?.map((item: IFormSchema) =>
                      item.component.type === "number" ? (
                        <NumberField
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          name={item.component.name}
                          required={item.required}
                          component={item.component}
                        />
                      ) : ["phone", "email", "text"].includes(
                          item?.component?.type ?? ""
                        ) ? (
                        <TextInput
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          name={item.component.name}
                          required={item.required}
                          component={item.component}
                        />
                      ) : item.component.type === "file" ? (
                        <FileField
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          name={item.component.name}
                          required={item.required}
                          component={item.component}
                        />
                      ) : item.component.type === "checkbox" ? (
                        <CheckboxField
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          name={item.component.name}
                          required={item.required}
                          component={item.component}
                        />
                      ) : item.component.type === "date" ? (
                        <DateField
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          name={item.component.name}
                          required={item.required}
                          component={item.component}
                        />
                      ) : ["radio", "select"].includes(
                          item.component.type ?? ""
                        ) ? (
                        <SelectField
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          name={item.component.name}
                          required={item.required}
                          component={item.component}
                        />
                      ) : undefined
                    )}

                    {currentStep === pages.length - 1 && (
                      <DataPolicyCheck
                        dataPolicyLink={values.dataPolicyLink}
                        dataUsageLink={values.dataUsageLink}
                      />
                    )}

                    {index === currentStep && (
                      <div className={styles.wrapper}>
                        {pages?.length > 1 ? (
                          <GeneralButton
                            onButtonClick={onBackStep}
                            disabled={currentStep === 0 ? true : false}
                            text="← Volver"
                            type="button"
                            background={currentStep === 0 ? "#FFFFFF" : color}
                            border={currentStep === 0 ? "#CACFD2" : color}
                            textColor={
                              currentStep === 0 ? "#9C9EA5" : "#FFFFFF"
                            }
                            cursor={currentStep === 0 ? "no-drop" : "pointer"}
                          />
                        ) : undefined}
                        <>
                          {currentStep === pages?.length - 1 ? (
                            <GeneralButton
                              onButtonClick={
                                userId ? onSubmitData : confirmData
                              }
                              disabled={false}
                              text="Enviar"
                              type="button"
                              background={color}
                              border={color}
                              textColor="#FFFFFF"
                              cursor="pointer"
                            />
                          ) : (
                            <GeneralButton
                              onButtonClick={onNextStep}
                              disabled={false}
                              text="Siguiente paso"
                              type="submit"
                              background={color}
                              border={color}
                              textColor="#FFFFFF"
                              cursor="pointer"
                            />
                          )}
                        </>
                      </div>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}

            {openModal && (
              <ModalDataConfirm
                openModal={openModal}
                setOpenModal={setOpenModal}
                onButtonSubmit={() => onSubmitData()}
                message="¿Deseas enviar los siguientes datos?"
                data={sendData}
              />
            )}

            {fieldsRequired && (
              <SnackbarRequiredFieldError
                fieldRequired={fieldsRequired}
                setFieldsRequired={setFieldsRequired}
              />
            )}

            {errorAxios && (
              <ModalError
                error={errorAxios.error}
                message={errorAxios.message}
              />
            )}
          </form>
        </FormProvider>
      ) : null}
    </>
  );
};

export default App;
